import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from './reducers';
import * as tourUtil from '../utils/tour';
import { InteractionStatus } from 'types';
import { sleep } from 'shared/utils/helpers';
import { selectActiveUser } from './shared-selectors';
import { selectActiveOrg } from './auth-slice';
import { AppDispatch } from './store';

enum TourFeedsKey {
  HIDE_SETUP_STEPS = 'sb-uls:SetupSteps',
  HIDE_TIPS_USAGE = 'sb-uls:TipsAndUsage',
}

export interface WidgetDemoMessage {
  type: string;
  data?: object;
}

export interface WidgetDemoMouse {
  x: number;
  y: number;
  click?: boolean;
}

// STATE
export const initialState = {
  isTouring: false,
  tourStepId: null as tourUtil.TourStepId | null,
  demoMessages: [] as WidgetDemoMessage[],
  demoMouse: { x: 50, y: 50, click: false } as WidgetDemoMouse,
  demoVideoSrc: null as string | null,

  // Spoofed realtime state
  isConnectedToPeer: false,
  callStatus: 'off' as InteractionStatus,
  takeoverStatus: 'off' as InteractionStatus,
  showSetupSteps: !localStorage.getItem(TourFeedsKey.HIDE_SETUP_STEPS),
  showTipsAndUsage: !localStorage.getItem(TourFeedsKey.HIDE_TIPS_USAGE),
};

export type TourState = typeof initialState;

// THUNKS
export const startTour = createAsyncThunk<
  void,
  tourUtil.TourStepId | undefined,
  { state: AppState }
>('tour/startTour', async (initialStep, { dispatch }) => {
  await tourUtil.startTour(dispatch as AppDispatch, initialStep);
});

export const endTour = createAsyncThunk<void, void, { state: AppState }>(
  'tour/endTour',
  async () => {
    await tourUtil.endTour();
  },
);

export const startTourDemoIframe = createAsyncThunk<void, void, { state: AppState }>(
  'tour/startTourDemoIframe',
  async (_, { dispatch }) => {
    await sleep(500);
    dispatch(setIsConnectedToPeer(true));
  },
);

export const syncOrgWithTourDemoIframe = createAsyncThunk<
  void,
  void,
  { state: AppState }
>('tour/syncOrgWithTourDemoIframe', async (_, { dispatch, getState }) => {
  const state = getState();
  const user = selectActiveUser(state);
  const org = selectActiveOrg(state);
  if (user && org) {
    const orgData = {
      agents: [
        {
          id: user.id,
          name: user.displayName,
          title: user.title,
          avatar: user.avatar?.imageUrl || '',
        },
      ],
      org: {
        id: org.id,
        widgetSettings: org.widgetSettings,
      },
    };
    dispatch(setDemoMessages([{ type: 'sbdemo:orgdata', data: orgData }]));
  }
});

export const startTourCall = createAsyncThunk<void, void, { state: AppState }>(
  'tour/startTourCall',
  async (_, { dispatch }) => {
    // Hide the tooltip while we start the call
    tourUtil.hideStepTooltip();
    dispatch(setDemoMessages([{ type: 'sbdemo:reset' }]));
    dispatch(setCallStatus('establishing'));
    try {
      // Pre-acquire the stream to prompt them for permissions.
      // Continue on even if they don't, since we can't guarantee.
      await tourUtil.setupTourCameraStream();
      dispatch(setDemoMessages([{ type: 'sbdemo:startcall' }]));
    } catch (err) {
      dispatch(
        setDemoMessages([
          { type: 'sbdemo:startcall' },
          { type: 'sbdemo:setagentmutes', data: { mic: true, cam: true } },
        ]),
      );
    }
    dispatch(setDemoMessages([{ type: 'sbdemo:startvideo' }]));
    await sleep(4600);
    dispatch(setDemoMouse({ x: 74, y: 42, click: true }));
    await sleep(400);
    dispatch(setDemoMessages([{ type: 'sbdemo:answercall' }]));
    dispatch(setCallStatus('active'));
    dispatch(setDemoVideoSrc(tourUtil.DEMO_VIDEOS.callStart.src));
    await sleep(1600);
    await sleep(400);
    await sleep(800);
    dispatch(setDemoMouse({ x: 64, y: 72, click: false }));
    await sleep(tourUtil.DEMO_VIDEOS.callStart.duration - 800);
    tourUtil.getActiveTour()?.next();
  },
);

export const resetTourCall = createAsyncThunk<void, void, { state: AppState }>(
  'tour/resetTourCall',
  async (_, { dispatch }) => {
    dispatch(setDemoMessages([{ type: 'sbdemo:reset' }]));
    dispatch(setCallStatus('off'));
    dispatch(setDemoVideoSrc(null));
  },
);

export const startTourTakeover = createAsyncThunk<void, void, { state: AppState }>(
  'tour/startTourTakeover',
  async (_, { dispatch }) => {
    tourUtil.hideStepTooltip();
    dispatch(setTakeoverStatus('establishing'));
    dispatch(setDemoMessages([{ type: 'sbdemo:starttakeover' }]));
    dispatch(setDemoVideoSrc(tourUtil.DEMO_VIDEOS.acceptTakeover.src));
    await sleep(1500);
    dispatch(setDemoMouse({ x: 52, y: 56, click: true }));
    await sleep(400);
    dispatch(setDemoMouse({ x: 54, y: 78, click: false }));
    dispatch(setDemoMessages([{ type: 'sbdemo:accepttakeover' }]));
    dispatch(setTakeoverStatus('active'));
    await sleep(tourUtil.DEMO_VIDEOS.acceptTakeover.duration - 2000);
    tourUtil.getActiveTour()?.next();
  },
);

export const resetTourTakeover = createAsyncThunk<void, void, { state: AppState }>(
  'tour/resetTourCall',
  async (_, { dispatch }) => {
    dispatch(setDemoMessages([{ type: 'sbdemo:endtakeover' }]));
    dispatch(setTakeoverStatus('off'));
  },
);

export const endTourCall = createAsyncThunk<void, void, { state: AppState }>(
  'tour/endTourCall',
  async (_, { dispatch }) => {
    tourUtil.hideStepTooltip();
    dispatch(setDemoMessages([{ type: 'sbdemo:reset' }]));
    dispatch(setCallStatus('off'));
    await sleep(300);
    tourUtil.getActiveTour()?.next();
  },
);

export const showSetupSteps = createAsyncThunk<void, void, { state: AppState }>(
  'tour/showSetupSteps',
  async (_, { dispatch }) => {
    localStorage.removeItem(TourFeedsKey.HIDE_SETUP_STEPS); /* scan:ignore */
    dispatch(setShowSetupSteps(true));
  },
);

export const hideSetupSteps = createAsyncThunk<void, void, { state: AppState }>(
  'tour/hideSetupSteps',
  async (_, { dispatch }) => {
    localStorage.setItem(TourFeedsKey.HIDE_SETUP_STEPS, 'true'); /* scan:ignore */
    dispatch(setShowSetupSteps(false));
  },
);

export const showTipsAndUsage = createAsyncThunk<void, void, { state: AppState }>(
  'tour/showTipsAndUsage',
  async (_, { dispatch }) => {
    localStorage.removeItem(TourFeedsKey.HIDE_TIPS_USAGE); /* scan:ignore */
    dispatch(setShowTipsAndUsage(true));
  },
);

export const hideTipsAndUsage = createAsyncThunk<void, void, { state: AppState }>(
  'tour/hideTipsAndUsage',
  async (_, { dispatch }) => {
    localStorage.setItem(TourFeedsKey.HIDE_TIPS_USAGE, 'true'); /* scan:ignore */
    dispatch(setShowTipsAndUsage(false));
  },
);

// SLICE
const tourSlice = createSlice({
  name: 'tour',
  initialState,
  reducers: {
    setIsTouring(state, action: PayloadAction<boolean>) {
      state.isTouring = action.payload;
      if (!action.payload) {
        state.tourStepId = null;
      }
    },
    setTourStepId(state, action: PayloadAction<tourUtil.TourStepId | null>) {
      state.tourStepId = action.payload;
    },
    setIsConnectedToPeer(state, action: PayloadAction<boolean>) {
      state.isConnectedToPeer = action.payload;
    },
    setCallStatus(state, action: PayloadAction<InteractionStatus>) {
      state.callStatus = action.payload;
    },
    setTakeoverStatus(state, action: PayloadAction<InteractionStatus>) {
      state.takeoverStatus = action.payload;
    },
    setDemoMessages(state, action: PayloadAction<WidgetDemoMessage[]>) {
      state.demoMessages = action.payload;
    },
    setDemoMouse(state, action: PayloadAction<WidgetDemoMouse>) {
      state.demoMouse = action.payload;
    },
    setDemoVideoSrc(state, action: PayloadAction<string | null>) {
      state.demoVideoSrc = action.payload;
    },
    setShowSetupSteps(state, action: PayloadAction<boolean>) {
      state.showSetupSteps = action.payload;
    },
    setShowTipsAndUsage(state, action: PayloadAction<boolean>) {
      state.showTipsAndUsage = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(startTour.fulfilled, state => {
      state.isTouring = true;
      state.demoMouse = { x: 50, y: 50, click: false };
      state.isConnectedToPeer = false;
      state.callStatus = 'off';
      state.takeoverStatus = 'off';
    });

    builder.addCase(endTour.fulfilled, state => {
      state.isTouring = false;
      state.tourStepId = null;
    });
  },
});

// SELECTORS
export const selectIsTouring = (s: AppState) => s.tour.isTouring;
export const selectTourStepId = (s: AppState) => s.tour.tourStepId;

export const selectTour = (s: AppState) => {
  if (s.tour.isTouring) {
    return tourUtil.getActiveTour();
  }
};

export const {
  setIsTouring,
  setTourStepId,
  setIsConnectedToPeer,
  setCallStatus,
  setTakeoverStatus,
  setDemoMessages,
  setDemoMouse,
  setDemoVideoSrc,
  setShowSetupSteps,
  setShowTipsAndUsage,
} = tourSlice.actions;
export default tourSlice.reducer;
